/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MyHubBadge } from '@app/app/page-modules/my-hub/models/my-hub.model';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Page } from '../../../shared/models/page';
import { RestClientService } from '../../../shared/services/rest-client.service';
import { ObservableResult } from '../../../shared/store';
import { Moment } from '../../folio/models';
import { EnrichedTask } from '../models/user-task.model';
import { MyHubDataService } from './data.service';

@Injectable()
export class ApiMyHubDataService implements MyHubDataService {
  private readonly myHubUrl = Location.joinWithSlash(environment.apiRootUrl || '', '/my-hub');
  private readonly tasksUrl = Location.joinWithSlash(environment.apiRootUrl || '', '/tasks');

  constructor(
    private client: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  getCommunityMoments(page: number, size: number): ObservableResult<Page<Moment>> {
    const params = {
      page: page.toString(),
      size: size.toString(),
    };
    return this.client.get<Page<Moment>>(`${this.myHubUrl}/community-moments`, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorLoadingCommunityMoments'))),
    );
  }

  getTasks(page: number, size: number): ObservableResult<Page<EnrichedTask>> {
    const params = {
      page: page.toString(),
      size: size.toString(),
    };
    return this.client.get<Page<EnrichedTask>>(this.tasksUrl, params).pipe(
      switchMap(({ body }) => ObservableResult.ofSuccess(body)),
      catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorLoadingTasks'))),
    );
  }

  getBadges(languageCode?: string): ObservableResult<MyHubBadge[]> {
    return this.client
      .get<
        MyHubBadge[]
      >(Location.joinWithSlash(environment.apiRootUrl || '', `/badges/issued`), null, languageCode ? { 'Accept-Language': languageCode } : null)
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadge'))),
      );
  }
}
